import { useCallback, useEffect, useState } from 'react';

import { useExperimentTrackCallback } from '@zenbusiness/application-commons-experiments';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import isTrue from '@zenbusiness/application-commons-utils/isTrue';
import Banner from '@zenbusiness/zui/Banner';
import Box from '@zenbusiness/zui/Box';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import { useTheme } from '@zenbusiness/zui/styles';
import { useNavigate } from 'react-router-dom';

import EnterEINNumber from '#client/components/EnterEINNumberModal';
import EnterOperatingAddressModal from '#client/components/EnterOperatingAddressModal';
import StatementsModal from '#client/components/StatementsModal';
import StickyBoxContainer from '#client/components/StickyBoxContainer';
import { DB_APPLICATION_STATUS, INITIAL_PAYMENT_STATUS } from '#client/constants/application';
import { RELEASE_PREVENT_BANNER_COVER_UP_TEXT } from '#client/constants/experimentsFlags';
import { MODAL_NAMES } from '#client/constants/modals';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBillingPath from '#client/hooks/useBillingPath';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useFeatureFlag from '#client/hooks/useFeatureFlag';
import { useLogError } from '#client/hooks/useLogError';
import useModalContext from '#client/hooks/useModalContext';
import useOrderTaskContext from '#client/hooks/useOrderTaskContext';
import useSCorpBankingPrice from '#client/hooks/useSCorpBankingPrice';
import {
  defaultPayloadBannerDisplayed,
  defaultPayloadButtonPressed
} from '#client/hooks/useTrackEvent';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { BannerWrapperStyled } from '#client/pages/SharedStyles';
import { GET_ID_OF_STATEMENTS_QUERY } from '#client/queries';
import { dispatchReloadCombinedProvider } from '#client/utils/event';

const CONTACT_US_URL = 'https://www.zenbusiness.com/contact-us/';
const INTERVAL_TIME_FOR_CHECKING_UNIT_ACCOUNT_ID = 5000;
const DELAY_RELOAD_PAGE = 100;

const BannerWrapper = ({ children }) => {
  const [isEnablePreventBannerCoverUpText, isLoadingFlag] = useFeatureFlag(
    RELEASE_PREVENT_BANNER_COVER_UP_TEXT
  );
  const flagIsOn = !isLoadingFlag && isEnablePreventBannerCoverUpText;
  if (flagIsOn) {
    return <Box mb="20px">{children}</Box>;
  }
  return <StickyBoxContainer>{children}</StickyBoxContainer>;
};

export default function ApplicationStatusBanner() {
  const trackExperiment = useExperimentTrackCallback();
  const { isOpeningModal } = useCheckOpenModal();
  const navigate = useNavigate();
  const { openModal } = useModalContext();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { bankingPrice } = useSCorpBankingPrice();
  const [intervalId, setIntervalId] = useState(null);
  const theme = useTheme();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const billingPath = useBillingPath();

  const {
    appStatus,
    loading: loadingAppStatus,
    initialPaymentStatus
  } = useApplicationStatusContext();
  const { isLoadingOrderTask, bankingPreferredTask } = useOrderTaskContext();
  const {
    isEmptyUnitAccountId,
    refetch: refetchUnitAccountId,
    isValidMfa,
    loading: loadingUnitAccountId,
    isFetchingMfaStatus
  } = useUnitAccountId();

  const isSkipQuery = appStatus !== DB_APPLICATION_STATUS.CLOSED;

  const {
    loading: loadingStatements,
    data: dataStatements,
    errors,
    error: errorStatements
  } = useQuery(GET_ID_OF_STATEMENTS_QUERY, {
    variables: {
      params: {
        businessUuid: businessEntityUuid,
        offset: 0,
        limit: 1
      }
    },
    skip: isSkipQuery,
    noThrow: true
  });

  useLogError(errors, errorStatements);
  const hasStatements = dataStatements?.statementListItems?.data?.length > 0;

  const renderSlots = useCallback(() => {
    const slotProps = {
      primaryAction: {
        label: 'Re-apply',
        clickstreamProps: {
          ...defaultPayloadButtonPressed,
          elementName: 'reapply_banking_application',
          elementText: 'Re-apply',
          appPageName: 'closed_account_banking_dashboard'
        },
        onClick: () => {
          navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`, {
            replace: true
          });
        }
      }
    };

    if (hasStatements) {
      slotProps.secondaryAction = {
        label: 'View statements',
        onClick: () =>
          openModal({
            component: StatementsModal
          })
      };
    }
    return slotProps;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStatements]);

  useEffect(() => {
    const clearIntervalId = () => {
      clearInterval(intervalId);
      setIntervalId(() => null);
    };

    const handleReload = () => {
      clearIntervalId();
      setTimeout(() => {
        dispatchReloadCombinedProvider();
      }, DELAY_RELOAD_PAGE);
    };

    if (
      isTrue(isValidMfa) &&
      appStatus === DB_APPLICATION_STATUS.APPROVED &&
      isEmptyUnitAccountId &&
      !intervalId &&
      !isOpeningModal
    ) {
      const tempInterval = setInterval(async () => {
        const response = await refetchUnitAccountId();
        // If unitAccountId is not empty, we need to reload the page
        const unitAccountId = response?.data?.partnerAccounts?.partnerAccountsIds?.[0];
        if (unitAccountId) {
          handleReload();
        }
      }, INTERVAL_TIME_FOR_CHECKING_UNIT_ACCOUNT_ID);
      setIntervalId(() => tempInterval);
    }

    if (
      intervalId &&
      (appStatus !== DB_APPLICATION_STATUS.APPROVED || !isEmptyUnitAccountId || isOpeningModal)
    ) {
      clearIntervalId();
    }

    return clearIntervalId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatus, isEmptyUnitAccountId, isOpeningModal]);

  const bannerDisplayedEventProps = {
    ...defaultPayloadBannerDisplayed,
    bannerType: 'promo',
    customDestinationPageName_: 'closed_account_banking_dashboard'
  };

  useEffect(() => {
    if (appStatus === DB_APPLICATION_STATUS.CLOSED) {
      refetchUnitAccountId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStatus]);

  if (
    loadingAppStatus ||
    loadingUnitAccountId ||
    isFetchingMfaStatus ||
    loadingStatements ||
    isLoadingOrderTask
  ) {
    return undefined;
  }

  switch (true) {
    // We only support re-apply for app status is denied AND banking order is canceled
    case appStatus === DB_APPLICATION_STATUS.DENIED && bankingPreferredTask?.status === 'canceled':
    case appStatus === DB_APPLICATION_STATUS.NOT_QUALIFIED:
      // Check for banking preferred order status = canceled first.
      // A notQualified app should have a canceled order status.
      return (
        <BannerWrapper>
          <Banner
            clickstreamProps={{
              bannerDisplayed: { ...bannerDisplayedEventProps, bannerPosition: 'top' }
            }}
            slotProps={renderSlots()}
            slots={{
              startIcon: (
                <SharedAsset
                  alt=""
                  src="/illustration/dollarsign-icon.svg"
                />
              )
            }}
            subtitle={`Get back to your business checking account for just ${bankingPrice}.`}
            title="Jump back into ZenBusiness Banking"
            variant="promo"
          />
        </BannerWrapper>
      );
    case appStatus === DB_APPLICATION_STATUS.APPROVED &&
      initialPaymentStatus === INITIAL_PAYMENT_STATUS.FAILED &&
      isEmptyUnitAccountId:
      return (
        <BannerWrapperStyled>
          <Banner
            clickstreamProps={{
              bannerDisplayed: {
                ...defaultPayloadBannerDisplayed,
                bannerType: 'info',
                appPageName: 'active_banking_dashboard',
                elementName: 'first_missed_payment_Account_not_setup'
              }
            }}
            slotProps={
              isDesktop
                ? {
                    primaryAction: {
                      label: 'Add payment method',
                      onClick: () => {
                        navigate(billingPath);
                      }
                    }
                  }
                : {}
            }
            subtitle="You've been approved, but to set up your account, add a payment method now."
            title="Finish setting up your account"
            variant="info"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.APPROVED &&
      initialPaymentStatus === INITIAL_PAYMENT_STATUS.FAILED &&
      !isEmptyUnitAccountId:
      return (
        <Banner
          clickstreamProps={{
            bannerDisplayed: {
              ...defaultPayloadBannerDisplayed,
              bannerType: 'info',
              appPageName: 'active_banking_dashboard',
              elementName: 'first_missed_payment_Account_active'
            }
          }}
          slotProps={
            isDesktop
              ? {
                  primaryAction: {
                    label: 'Update now',
                    onClick: () => {
                      navigate(billingPath);
                    }
                  }
                }
              : {}
          }
          subtitle="To keep your bank account active, we need a payment method for the $5 monthly fee. If you have questions, call (844) 493-6249."
          title="Please update your account info"
          variant="alert"
        />
      );
    case appStatus === DB_APPLICATION_STATUS.APPROVED:
      // The banner will be shown when the account approved but
      // somehow the Unit not create the unitAccountId yet.
      if (isEmptyUnitAccountId) {
        return (
          <BannerWrapperStyled>
            <Banner
              slotProps={
                isDesktop
                  ? {
                      primaryAction: {
                        label: 'Contact us',
                        to: CONTACT_US_URL
                      }
                    }
                  : {}
              }
              subtitle="You’re approved and your bank account is setting up. Check back later or contact us."
              title="Your account will be ready soon"
            />
          </BannerWrapperStyled>
        );
      }
      return null;
    case appStatus === DB_APPLICATION_STATUS.CANCELED:
      return (
        <BannerWrapper>
          <Banner
            clickstreamProps={{ bannerDisplayed: bannerDisplayedEventProps }}
            slotProps={{
              primaryAction: {
                label: 'Re-apply',
                clickstreamProps: {
                  ...defaultPayloadButtonPressed,
                  elementName: 'reapply_banking_application',
                  elementText: 'Re_apply',
                  pageType: 'closed_account_banking_dashboard'
                },
                onClick: () => {
                  navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`, {
                    replace: true
                  });
                }
              }
            }}
            slots={{
              startIcon: (
                <SharedAsset
                  alt=""
                  src="/illustration/dollarsign-icon.svg"
                />
              )
            }}
            subtitle={`Get back to your business checking account for just ${bankingPrice}.`}
            title="Jump back into ZenBusiness Banking"
            variant="promo"
          />
        </BannerWrapper>
      );
    case appStatus === DB_APPLICATION_STATUS.EXPIRED:
      return (
        <BannerWrapper>
          <Banner
            clickstreamProps={{ bannerDisplayed: bannerDisplayedEventProps }}
            slotProps={{
              primaryAction: {
                label: 'Restart application',
                onClick: () => {
                  navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`, {
                    replace: true
                  });
                }
              }
            }}
            slots={{
              startIcon: (
                <SharedAsset
                  alt=""
                  src="/illustration/dollarsign-icon.svg"
                />
              )
            }}
            subtitle="Get back to your business checking account."
            title="Jump back into ZenBusiness Banking"
            variant="promo"
          />
        </BannerWrapper>
      );
    case appStatus === DB_APPLICATION_STATUS.NOT_STARTED:
      return (
        <BannerWrapper>
          <Banner
            clickstreamProps={{ bannerDisplayed: bannerDisplayedEventProps }}
            slotProps={{
              secondaryAction: {
                label: 'Apply now',
                clickstreamProps: {
                  ...defaultPayloadButtonPressed,
                  elementName: 'Banking_application_CTA',
                  elementText: 'Apply Now',
                  pageCategory: 'dashboard'
                },
                onClick: () => {
                  trackExperiment('Click apply now to banking');
                  navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`, {
                    replace: true
                  });
                }
              }
            }}
            slots={{
              startIcon: (
                <SharedAsset
                  alt=""
                  src="/illustration/dollarsign-icon.svg"
                />
              )
            }}
            subtitle={`Get business checking for ${bankingPrice}, and no minimum deposit or balance to keep.`}
            title="Bank with ZenBusiness"
            variant="promo"
          />
        </BannerWrapper>
      );
    case appStatus === DB_APPLICATION_STATUS.INCOMPLETE:
    case appStatus === DB_APPLICATION_STATUS.STARTED:
    case appStatus === DB_APPLICATION_STATUS.AWAITING_CHECKOUT:
      return (
        <BannerWrapperStyled>
          <Banner
            clickstreamProps={{ bannerDisplayed: bannerDisplayedEventProps }}
            slotProps={{
              primaryAction: {
                label: 'Finish application',
                onClick: () => {
                  navigate(`?modal=${MODAL_NAMES.APPLICATION_INIT_MODAL}`, {
                    replace: true
                  });
                }
              }
            }}
            subtitle="You’re so close! Finish your application, and you could be approved today."
            title="Finish getting a business bank account"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.PENDING_REVIEW:
    case appStatus === DB_APPLICATION_STATUS.PENDING:
    case appStatus === DB_APPLICATION_STATUS.DENIED:
      return (
        <BannerWrapperStyled>
          <Banner
            subtitle="You’ll get an email in 1 to 3 business days if you were approved or we need more info."
            title="Your application is processing"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.SUBMITTED:
      return (
        <BannerWrapperStyled>
          <Banner
            clickstreamProps={{
              bannerDisplayed: {
                ...defaultPayloadBannerDisplayed,
                bannerType: 'info',
                appPageName: 'active_banking_dashboard',
                elementName: 'application_pending_banking'
              }
            }}
            subtitle="You’ll get an email in 1 to 3 business days if you were approved or we need more info."
            title="Your application is pending"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.AWAITING_BANKING_ORDER_COMPLETION:
      return (
        <BannerWrapperStyled>
          <Banner
            subtitle="Your formation needs to be complete before we can process your application."
            title="Your application is pending"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.AWAITING_EIN_ORDER_COMPLETION:
      return (
        <BannerWrapperStyled>
          <Banner
            subtitle="Both your formation and EIN (Employer ID number) need to be complete before we can process your application."
            title="Your application is pending"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.AWAITING_EIN_ORDER_CREATION:
    case appStatus === DB_APPLICATION_STATUS.AWAITING_EIN:
      return (
        <BannerWrapperStyled>
          <Banner
            clickstreamProps={{
              bannerDisplayed: {
                ...defaultPayloadBannerDisplayed,
                bannerType: 'info',
                appPageName: 'active_banking_dashboard',
                elementName: 'application_paused_missing_EIN_banking'
              }
            }}
            slotProps={{
              primaryAction: {
                label: 'Enter EIN',
                clickstreamProps: {
                  ...defaultPayloadButtonPressed,
                  elementName: 'enter_EIN_from_banner',
                  elementText: 'Enter EIN',
                  appPageName: 'active_banking_dashboard'
                },
                onClick: () => {
                  openModal({
                    component: EnterEINNumber
                  });
                }
              }
            }}
            slots={{
              startIcon: (
                <SharedAsset
                  alt=""
                  src="/icon/micro/alert-fill.svg"
                />
              )
            }}
            subtitle="We need your EIN (Employer ID number) to finish processing your application"
            sx={() => ({ backgroundColor: theme.palette.background.grey.light })}
            title="Your application is paused"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.AWAITING_OPERATING_ADDRESS:
      return (
        <BannerWrapperStyled>
          <Banner
            clickstreamProps={{
              bannerDisplayed: {
                ...defaultPayloadBannerDisplayed,
                bannerType: 'info',
                appPageName: 'active_banking_dashboard',
                elementName: 'application_paused_missing_operating_address_banking'
              }
            }}
            slotProps={{
              primaryAction: {
                label: 'Enter Operating Address',
                clickstreamProps: {
                  ...defaultPayloadButtonPressed,
                  elementName: 'enter_operating_address_from_banner',
                  elementText: 'Enter Operating Address',
                  appPageName: 'active_banking_dashboard'
                },
                onClick: () => {
                  openModal({
                    component: EnterOperatingAddressModal
                  });
                }
              }
            }}
            slots={{
              startIcon: (
                <SharedAsset
                  alt=""
                  src="/icon/micro/alert-fill.svg"
                />
              )
            }}
            subtitle="We need your operating address to finish processing your application"
            sx={() => ({ backgroundColor: theme.palette.background.grey.light })}
            title="Your application is paused"
          />
        </BannerWrapperStyled>
      );
    case appStatus === DB_APPLICATION_STATUS.CLOSED:
      // When unit account is empty and data statements different undefined => this banner is show
      if (isEmptyUnitAccountId && dataStatements) {
        return (
          <BannerWrapper>
            <Banner
              clickstreamProps={{
                bannerDisplayed: bannerDisplayedEventProps
              }}
              slotProps={renderSlots()}
              slots={{
                startIcon: (
                  <SharedAsset
                    alt=""
                    src="/illustration/dollarsign-icon.svg"
                  />
                )
              }}
              subtitle={`Get back to your business checking account for just ${bankingPrice}.`}
              title="Jump back into ZenBusiness Banking"
              variant="promo"
            />
          </BannerWrapper>
        );
      }
      return null;
    default:
      return null;
  }
}
